import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const Banner = ({ config }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Safe defaults
  const defaultConfig = {
    bannerpreset: 'full-width',
    autoPlay: true,
    interval: 5000,
    confetti: {
      enabled: false,
      trigger: 'onLoad',
      particleCount: 100,
      spread: 70,
      originY: 0.6,
      colors: '',
      ticks: 200,
      gravity: 0.8,
      decay: 0.94,
      scalar: 1,
      shapes: 'square,circle',
    },
    slides: [],
    leftPanel: {
      title: "Best Sellers",
      subtitle: "Featured",
      description: "Explore our most loved products",
      ctaText: "View All",
      ctaLink: "/",
      bgClass: "bg-gray-900",
      image: null,
      imageOverlayColor: "black",
      imageOverlayOpacity: 20,
      contentPadding: "p-8",
      titleStyle: { color: '#ffffff', fontSize: 'text-2xl', fontWeight: 'font-light', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-sm', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      descriptionStyle: { color: '#ffffff', fontSize: 'text-base', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      ctaStyle: { bgColor: 'bg-white', textColor: 'text-gray-900' },
    },
    rightTopPanel: {
      title: "Up to 40% Off",
      subtitle: "Sale",
      description: "",
      ctaText: "Shop Sale →",
      ctaLink: "/",
      bgClass: "bg-gradient-to-br from-pink-50 to-rose-100",
      image: null,
      imageOverlayColor: "black",
      imageOverlayOpacity: 20,
      contentPadding: "p-8",
      titleStyle: { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-medium', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#111827', fontSize: 'text-sm', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      descriptionStyle: { color: '#111827', fontSize: 'text-base', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      ctaStyle: { bgColor: '', textColor: 'text-gray-900 underline' },
    },
    rightBottomPanel: {
      title: "Fresh Arrivals",
      subtitle: "New",
      description: "",
      ctaText: "Discover →",
      ctaLink: "/",
      bgClass: "bg-gradient-to-br from-violet-50 to-purple-100",
      image: null,
      imageOverlayColor: "black",
      imageOverlayOpacity: 20,
      contentPadding: "p-8",
      titleStyle: { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-medium', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#111827', fontSize: 'text-sm', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      descriptionStyle: { color: '#111827', fontSize: 'text-base', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      ctaStyle: { bgColor: '', textColor: 'text-gray-900 underline' },
    },
    asymmetricSidebar: {
      top: {
        title: "Member Exclusive",
        subtitle: "Exclusive",
        description: "Join now for early access",
        ctaText: "Sign Up",
        ctaLink: "/",
        bgClass: "bg-gray-900",
        image: null,
        imageOverlayColor: "black",
        imageOverlayOpacity: 20,
        contentPadding: "p-8",
        titleStyle: { color: '#ffffff', fontSize: 'text-xl', fontWeight: 'font-medium', fontFamily: 'font-sans' },
        subtitleStyle: { color: '#ffffff', fontSize: 'text-sm', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        descriptionStyle: { color: '#ffffff', fontSize: 'text-base', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        ctaStyle: { bgColor: 'bg-white', textColor: 'text-gray-900' },
      },
      bottom: {
        title: "Flash Deal",
        subtitle: "Limited",
        description: "24 hours only",
        ctaText: "Shop Now",
        ctaLink: "/",
        bgClass: "bg-gradient-to-br from-amber-50 to-yellow-100",
        image: null,
        imageOverlayColor: "black",
        imageOverlayOpacity: 20,
        contentPadding: "p-8",
        titleStyle: { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-medium', fontFamily: 'font-sans' },
        subtitleStyle: { color: '#111827', fontSize: 'text-sm', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        descriptionStyle: { color: '#111827', fontSize: 'text-base', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        ctaStyle: { bgColor: 'bg-gray-900', textColor: 'text-white' },
      },
    },
    customStyles: { container: { padding: '', margin: '', borderRadius: 'rounded-lg', boxShadow: '' } },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const {
    bannerpreset,
    slides = [],
    autoPlay,
    interval,
    confetti: confettiConfig,
    leftPanel,
    rightTopPanel,
    rightBottomPanel,
    asymmetricSidebar,
    customStyles,
  } = finalConfig;

  const containerStyle = customStyles.container || {};

  // Auto-play
  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, slides.length]);

  // Confetti
  const fireConfetti = () => {
    if (!confettiConfig.enabled) return;

    let colorsArray = undefined;
    if (confettiConfig.colors && confettiConfig.colors.trim()) {
      colorsArray = confettiConfig.colors.split(',').map(c => c.trim()).filter(c => c.startsWith('#'));
    }

    let shapesArray = ['square', 'circle'];
    if (confettiConfig.shapes && confettiConfig.shapes.trim()) {
      shapesArray = confettiConfig.shapes.split(',').map(s => s.trim());
    }

    confetti({
      particleCount: confettiConfig.particleCount || 100,
      spread: confettiConfig.spread || 70,
      origin: { y: confettiConfig.originY || 0.6 },
      colors: colorsArray,
      ticks: confettiConfig.ticks || 200,
      gravity: confettiConfig.gravity || 0.8,
      decay: confettiConfig.decay || 0.94,
      scalar: confettiConfig.scalar || 1,
      shapes: shapesArray,
    });
  };

  useEffect(() => {
    if (confettiConfig.trigger === 'onLoad') fireConfetti();
  }, []);

  useEffect(() => {
    if (confettiConfig.trigger === 'onSlideChange' && slides[currentSlide]?.triggerConfetti) {
      fireConfetti();
    }
  }, [currentSlide]);

  const handleCTAClick = (item) => {
    if (confettiConfig.trigger === 'onCTA' && item.triggerConfetti) fireConfetti();
    if (item.ctaLink) window.location.href = item.ctaLink;
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goToSlide = (index) => setCurrentSlide(index);

  if (slides.length === 0) return null;

  const currentSlideData = slides[currentSlide];

  // Render individual slide with full typography support
  const renderSlide = (slide) => (
    <div
      className={`min-w-full h-full flex items-center justify-center relative ${slide.background || ''}`}
      style={{
        backgroundImage: slide.image ? `url(${slide.image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className={`text-center max-w-4xl z-10 ${slide.contentPadding || 'p-10'}`}>
        {slide.subtitle && (
          <p className={`mb-3 tracking-wide uppercase ${slide.subtitleStyle.fontSize || 'text-base'} ${slide.subtitleStyle.fontWeight || 'font-light'} ${slide.subtitleStyle.fontFamily || 'font-sans'}`} style={{ color: slide.subtitleStyle.color }}>
            {slide.subtitle}
          </p>
        )}
        <h1 className={`mb-4 md:mb-6 tracking-tight ${slide.titleStyle.fontSize || 'text-6xl'} ${slide.titleStyle.fontWeight || 'font-light'} ${slide.titleStyle.fontFamily || 'font-sans'}`} style={{ color: slide.titleStyle.color }}>
          {slide.title}
        </h1>
        {slide.description && (
          <p className={`mb-6 md:mb-8 max-w-2xl mx-auto ${slide.descriptionStyle.fontSize || 'text-lg'} ${slide.descriptionStyle.fontWeight || 'font-normal'} ${slide.descriptionStyle.fontFamily || 'font-sans'}`} style={{ color: slide.descriptionStyle.color }}>
            {slide.description}
          </p>
        )}
        <button
          onClick={() => handleCTAClick(slide)}
          className={`px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-medium transition-colors hover:opacity-90 transform hover:scale-105 active:scale-95 ${slide.ctaStyle.bgColor || 'bg-gray-900'} ${slide.ctaStyle.textColor || 'text-white'}`}
        >
          {slide.ctaText}
        </button>
      </div>
      {slide.image && <div className={`absolute inset-0 bg-${slide.imageOverlayColor || 'black'}/${slide.imageOverlayOpacity || 20}`} />}
    </div>
  );

  // Generalized panel render
  const renderPanel = (panel, className = '') => (
    <div
      className={`${panel.bgClass || ''} rounded-lg flex flex-col justify-between relative ${className}`}
      style={{
        backgroundImage: panel.image ? `url(${panel.image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className={`${panel.contentPadding || 'p-8'} z-10`}>
        {panel.subtitle && (
          <p className={`text-sm mb-2 uppercase opacity-70 ${panel.subtitleStyle.fontSize || 'text-sm'} ${panel.subtitleStyle.fontWeight || 'font-normal'} ${panel.subtitleStyle.fontFamily || 'font-sans'}`} style={{ color: panel.subtitleStyle.color }}>
            {panel.subtitle}
          </p>
        )}
        <h3 className={`${panel.titleStyle.fontSize || 'text-2xl'} ${panel.titleStyle.fontWeight || 'font-light'} mb-4 ${panel.titleStyle.fontFamily || 'font-sans'}`} style={{ color: panel.titleStyle.color }}>
          {panel.title}
        </h3>
        {panel.description && (
          <p className={`mb-6 opacity-90 ${panel.descriptionStyle.fontSize || 'text-base'} ${panel.descriptionStyle.fontWeight || 'font-normal'} ${panel.descriptionStyle.fontFamily || 'font-sans'}`} style={{ color: panel.descriptionStyle.color }}>
            {panel.description}
          </p>
        )}
        <button
          onClick={() => handleCTAClick(panel)}
          className={`px-8 py-3 rounded-full font-medium transform hover:scale-105 active:scale-95 transition ${panel.ctaStyle.bgColor || ''} ${panel.ctaStyle.textColor || 'text-gray-900'}`}
        >
          {panel.ctaText}
        </button>
      </div>
      {panel.image && <div className={`absolute inset-0 bg-${panel.imageOverlayColor || 'black'}/${panel.imageOverlayOpacity || 20}`} />}
    </div>
  );

  const renderCarousel = (heightClass = 'h-[500px] md:h-[600px]', colSpan = '') => (
    <div className={`relative overflow-hidden ${containerStyle.boxShadow || ''}`} style={{ padding: containerStyle.padding, margin: containerStyle.margin }}>
      <div className={`flex transition-transform duration-700 ease-in-out ${heightClass} ${colSpan}`} style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
        {slides.map((s, i) => (
          <div key={i} className="min-w-full">
            {renderSlide(s)}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={prevSlide} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg z-20 transform hover:scale-110 transition">
            <ChevronLeft className="w-6 h-6 text-gray-900" />
          </button>
          <button onClick={nextSlide} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg z-20 transform hover:scale-110 transition">
            <ChevronRight className="w-6 h-6 text-gray-900" />
          </button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {slides.map((_, i) => (
              <button key={i} onClick={() => goToSlide(i)} className={`w-2 h-2 rounded-full transition transform hover:scale-125 ${i === currentSlide ? 'bg-gray-900 w-8' : 'bg-gray-400'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Full Width
  if (bannerpreset === 'full-width') {
    return renderCarousel();
  }

  // Three Column
  if (bannerpreset === 'three-column') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-10" style={containerStyle}>
        {renderPanel(leftPanel)}

        {renderCarousel('h-[400px] md:h-[500px]')}

        <div className="space-y-6">
          {renderPanel(rightTopPanel)}
          {renderPanel(rightBottomPanel)}
        </div>
      </div>
    );
  }

  // Asymmetric
  if (bannerpreset === 'asymmetric') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-10" style={containerStyle}>
        <div className="md:col-span-9">
          {renderCarousel('h-[400px] md:h-[450px]')}
        </div>
        <div className="md:col-span-3 space-y-6">
          {renderPanel(asymmetricSidebar.top)}
          {renderPanel(asymmetricSidebar.bottom)}
        </div>
      </div>
    );
  }

  return null;
};

export default Banner;